if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
      this.initDeliveryDateSelection();
      this.initCheckoutObserver(); // Intercept cart submission and redirect
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.querySelector('cart-delivery-date').classList.remove('empty');
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState) {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        const sectionHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        HTMLUpdateUtility.setInnerHTML(sectionElement, sectionHTML);
      });

      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      }, 2500);
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
        {
          id: 'pre-checkout',
          section: 'pre-checkout',
          selector: '.pre-checkout__cart',
        },
        {
          id: 'cart-toast-product',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    initDeliveryDateSelection() {
      const attachListener = () => {
        const deliveryDateRequiredBtn = this.querySelector('[data-delivery-date-required]');
        const deliveryDateSelector = this.querySelector('cart-delivery-date');

        if (!deliveryDateSelector) return;

        // deliveryDateRequiredBtn is only present in the cart drawer if a delivery date is not set
        if (!deliveryDateRequiredBtn) {
          deliveryDateSelector.classList.remove('empty');
        } else {
          deliveryDateRequiredBtn.addEventListener('click', () => {
            deliveryDateSelector.classList.add('empty');
            deliveryDateSelector.querySelector('input').focus();
            deliveryDateSelector.show();
          });
        }
      };

      attachListener();

      const observer = new MutationObserver(() => {
        attachListener();
      });

      observer.observe(this, { childList: true, subtree: true });
    }

    initCheckoutObserver() {
      const attachListener = () => {
        const form = this.querySelector('#CartDrawer-Form');
        const checkoutBtn = this.querySelector('[name="checkout"]');

        if (!form || !checkoutBtn) return;

        form.addEventListener(
          'submit',
          async (e) => {
            e.preventDefault();

            if (!this.validateDeliveryDate(form)) {
              return false;
            }

            await this.postForm(form);

            window.location.href = `/pages/${checkoutBtn.dataset.redirectTarget}`;
          }
        );
      };

      attachListener();

      // We need to rebind our event listener whenever we detect replaced nodes
      const observer = new MutationObserver(() => {
        attachListener();
      });

      observer.observe(this, { childList: true, subtree: true });
    }

    validateDeliveryDate(form) {
      const deliveryDateElement = form.closest('cart-drawer-items').parentElement.querySelector('cart-delivery-date');
      
      if (!deliveryDateElement || !deliveryDateElement.input || !deliveryDateElement.input.value) return false;

      const [day, month, year] = deliveryDateElement.input.value.split('-');
      const deliveryDate = new Date(`${year}-${month}-${day}`);

      const isValid = deliveryDate >= deliveryDateElement.minDate;

      if (!isValid) {
        deliveryDateElement.classList.add('error');
        deliveryDateElement.show();
      }

      return isValid;;
    }

    async postForm(form) {
      if (!form) return;
      const formData = new FormData(form);
      const deliveryDate = formData.get('attributes[delivery_date]');

      // If the delivery date is in the format DD-MM-YYYY, convert it to YYYY-MM-DD
      if (deliveryDate && deliveryDate.match(/^\d{2}\-\d{2}\-\d{4}$/)) {
        formData.set('attributes[delivery_date]', `${deliveryDate.split('-').reverse().join('-')}`);
      }

      await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        body: formData,
      });
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
        {
          id: 'pre-checkout',
          section: 'pre-checkout',
          selector: '.pre-checkout__cart',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
